import { useState, useEffect, type ReactNode } from 'react';
import { Drawer } from 'antd';
import { CloseOutlined, DownOutlined, CalendarOutlined, UserOutlined, ThunderboltFilled, CheckOutlined } from '@ant-design/icons';
import Calendar from '../calendar';
import TeamMemberSelect from '../teamMemberSelect';
import GroupSelect from '../groupSelect';
import SourceSelect from '../sourceSelect';
import LeadStageSelect from '../leadStageSelect';
import CustomFieldSelect, { type CustomOption } from '../customFieldSelect';
import './styles.scss';

interface DateRange {
  start: Date | null;
  end: Date | null;
  label?: string;   // preset label (Today / Upcoming / …) shown verbatim in the row
}

interface FilterOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

interface FilterItem {
  label: string;
  value: string;
  type: 'date' | 'team' | 'select';
  component: 'calendar' | 'teamMember' | 'group' | 'source' | 'leadStage' | 'custom' | null;
  options?: FilterOption[];
  datePresets?: boolean;   // show Follow-Up-style quick presets in the calendar
  customOptions?: CustomOption[];   // values for a custom-field multi-select
}

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Which section to auto-open when the drawer opens. */
  initialSection?: 'none' | 'assignedTo';
  /** Called with the applied filter values when the user hits APPLY. */
  onApply?: (filters: AppliedFilters) => void;
}

export interface AppliedFilters {
  assignedTo:    string[];       // member ids
  status:        string[];       // lead stages (+ 'none')
  source:        string[];       // lead sources
  followUp?:     string;         // bucket: today | upcoming | outstanding | someday | none
  followUpDate?: string;         // exact YYYY-MM-DD
  startDate?:    string;         // date-created range start
  endDate?:      string;         // date-created range end
  assignedStartDate?: string;    // date-assigned range start
  assignedEndDate?:   string;    // date-assigned range end
  count:         number;
}

const filterItems: FilterItem[] = [
  { label: 'Date Created',      value: 'any', type: 'date',   component: 'calendar' },
  { label: 'Assigned To',       value: 'any', type: 'team',   component: 'teamMember' },
  { label: 'Date Assigned',     value: 'any', type: 'date',   component: 'calendar' },
  { label: 'Follow Up',         value: 'any', type: 'date',   component: 'calendar', datePresets: true },
  { label: 'Last Activity',     value: 'any', type: 'date',   component: 'calendar' },
  { label: 'Contacted Status',  value: 'any', type: 'select', component: null, options: [
    { value: 'any',         label: 'Any' },
    { value: 'contacted',   label: 'Contacted',   icon: <UserOutlined /> },
    { value: 'uncontacted', label: 'Uncontacted', icon: <ThunderboltFilled /> },
  ] },
  { label: 'Source',            value: 'any', type: 'select', component: 'source' },
  { label: 'Groups',            value: 'any', type: 'select', component: 'group' },
  { label: 'Lead Stage',        value: 'any', type: 'select', component: 'leadStage' },
  { label: 'testing',           value: 'any', type: 'select', component: 'custom', customOptions: [
    { id: 'test1', label: 'test1' },
    { id: 'test2', label: 'test2' },
  ] },
];

const FilterDrawer = ({ open, onClose, initialSection = 'none', onApply }: FilterDrawerProps) => {
  const [activeFilter, setActiveFilter]             = useState<number | null>(null);
  const [teamMemberModalOpen, setTeamMemberModalOpen] = useState(false);
  const [dateFilters, setDateFilters]               = useState<Record<number, DateRange | null>>({ 0: null, 2: null, 3: null, 4: null });
  const [selectedMembers, setSelectedMembers]       = useState<unknown[]>([]);
  const [selectValues, setSelectValues]             = useState<Record<number, string>>({});
  const [groupModalOpen, setGroupModalOpen]         = useState(false);
  const [selectedGroups, setSelectedGroups]         = useState<string[]>([]);
  const [sourceModalOpen, setSourceModalOpen]       = useState(false);
  const [selectedSources, setSelectedSources]       = useState<string[]>([]);
  const [stageModalOpen, setStageModalOpen]         = useState(false);
  const [selectedStages, setSelectedStages]         = useState<string[]>([]);
  const [customModalIndex, setCustomModalIndex]     = useState<number | null>(null);
  const [customValues, setCustomValues]             = useState<Record<number, string[]>>({});

  const fmtDate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const formatDateRange = (dateRange: DateRange): string => {
    if (dateRange.label) return dateRange.label;   // preset (Today / Someday / …)
    if (!dateRange.start || !dateRange.end) return 'Any';
    return `${fmtDate(dateRange.start)} - ${fmtDate(dateRange.end)}`;
  };

  // Follow-Up preset label → API bucket value.
  const FOLLOWUP_BUCKET: Record<string, string> = {
    'Today': 'today', 'Upcoming': 'upcoming', 'Outstanding': 'outstanding',
    'Someday': 'someday', 'No Value set': 'none',
  };

  // Gather every drawer filter into the API-shaped payload (overrides win over state).
  const collectFilters = (
    overrides: Partial<{ assignedTo: string[]; stages: string[]; sources: string[] }> = {},
  ): AppliedFilters => {
    const members = overrides.assignedTo ?? (selectedMembers as string[]);
    const stages  = overrides.stages ?? selectedStages;
    const sources = overrides.sources ?? selectedSources;

    const status = stages.map(s => (s === '__none__' ? 'none' : s));

    const followUpVal = dateFilters[3];
    let followUp: string | undefined;
    let followUpDate: string | undefined;
    if (followUpVal) {
      if (followUpVal.label) followUp = FOLLOWUP_BUCKET[followUpVal.label];
      else if (followUpVal.start) followUpDate = fmtDate(followUpVal.start);
    }

    const dateCreated = dateFilters[0];
    let startDate: string | undefined;
    let endDate: string | undefined;
    if (dateCreated?.start && dateCreated?.end) {
      startDate = fmtDate(dateCreated.start);
      endDate   = fmtDate(dateCreated.end);
    }

    const dateAssigned = dateFilters[2];
    let assignedStartDate: string | undefined;
    let assignedEndDate: string | undefined;
    if (dateAssigned?.start && dateAssigned?.end) {
      assignedStartDate = fmtDate(dateAssigned.start);
      assignedEndDate   = fmtDate(dateAssigned.end);
    }

    return { assignedTo: members, status, source: sources, followUp, followUpDate, startDate, endDate, assignedStartDate, assignedEndDate, count: activeFilterCount };
  };

  const handleFilterClick = (item: FilterItem, index: number) => {
    if (item.component === 'group') {
      setActiveFilter(null);
      setTeamMemberModalOpen(false);
      setSourceModalOpen(false);
      setStageModalOpen(false);
      setCustomModalIndex(null);
      setGroupModalOpen(prev => !prev);        // toggle
    } else if (item.component === 'source') {
      setActiveFilter(null);
      setTeamMemberModalOpen(false);
      setGroupModalOpen(false);
      setStageModalOpen(false);
      setCustomModalIndex(null);
      setSourceModalOpen(prev => !prev);       // toggle
    } else if (item.component === 'leadStage') {
      setActiveFilter(null);
      setTeamMemberModalOpen(false);
      setGroupModalOpen(false);
      setSourceModalOpen(false);
      setCustomModalIndex(null);
      setStageModalOpen(prev => !prev);        // toggle
    } else if (item.component === 'custom') {
      setActiveFilter(null);
      setTeamMemberModalOpen(false);
      setGroupModalOpen(false);
      setSourceModalOpen(false);
      setStageModalOpen(false);
      setCustomModalIndex(prev => (prev === index ? null : index));   // toggle this one
    } else if (item.type === 'date' || (item.type === 'select' && item.options)) {
      setTeamMemberModalOpen(false);
      setGroupModalOpen(false);
      setSourceModalOpen(false);
      setStageModalOpen(false);
      setCustomModalIndex(null);
      setActiveFilter(activeFilter === index ? null : index);
    } else if (item.type === 'team') {
      setActiveFilter(null);
      setGroupModalOpen(false);
      setSourceModalOpen(false);
      setStageModalOpen(false);
      setCustomModalIndex(null);
      setTeamMemberModalOpen(prev => !prev);   // toggle: click again closes it
    }
  };

  const handleSelectOption = (index: number, value: string) => {
    setSelectValues(prev => ({ ...prev, [index]: value }));
    setActiveFilter(null);
  };

  const handleDateChange = (dateRange: DateRange, filterIndex: number) => {
    setDateFilters(prev => ({ ...prev, [filterIndex]: dateRange }));
    setActiveFilter(null);
  };

  const handleClearDate = (filterIndex: number) => {
    setDateFilters(prev => ({ ...prev, [filterIndex]: null }));
  };

  const handleMemberSelect = (members: unknown[]) => {
    setSelectedMembers(members);
    setTeamMemberModalOpen(false);   // DONE closes the popup, revealing the Assigned To pill
  };

  // "Clear" on Assigned To — clear members AND refetch (parent) without the param.
  const handleClearAssigned = () => {
    setSelectedMembers([]);
    onApply?.(collectFilters({ assignedTo: [] }));
  };

  // Count of filter *categories* that hold a non-default value.
  const activeFilterCount =
    Object.values(dateFilters).filter(Boolean).length +
    (selectedMembers.length ? 1 : 0) +
    Object.values(selectValues).filter(v => v && v !== 'any').length +
    (selectedGroups.length ? 1 : 0) +
    (selectedSources.length ? 1 : 0) +
    (selectedStages.length ? 1 : 0) +
    Object.values(customValues).filter(v => v && v.length).length;

  const handleReset = () => {
    setDateFilters({ 0: null, 2: null, 3: null, 4: null });
    setSelectedMembers([]);
    setSelectValues({});
    setSelectedGroups([]);
    setSelectedSources([]);
    setSelectedStages([]);
    setCustomValues({});
    setActiveFilter(null);
    setTeamMemberModalOpen(false);
    setGroupModalOpen(false);
    setSourceModalOpen(false);
    setStageModalOpen(false);
    setCustomModalIndex(null);
    // Clear parent filters too so the leads API returns all data.
    onApply?.({ assignedTo: [], status: [], source: [], count: 0 });
  };

  const handleApply = () => {
    onApply?.(collectFilters());
    onClose();
  };

  useEffect(() => {
    setGroupModalOpen(false);
    setSourceModalOpen(false);
    setStageModalOpen(false);
    setCustomModalIndex(null);
    if (!open) {
      setActiveFilter(null);
      setTeamMemberModalOpen(false);
      return;
    }
    // Filter button → nothing open; "All Team Members" → open Assigned To.
    setActiveFilter(null);
    setTeamMemberModalOpen(initialSection === 'assignedTo');
  }, [open, initialSection]);

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--sider-text)' }}>Filters</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
            {activeFilterCount > 0 && (
              <span className="reset-filters" onClick={handleReset}>RESET FILTERS</span>
            )}
            <CloseOutlined style={{ fontSize: '20px', cursor: 'pointer', color: '#999' }} onClick={onClose} />
          </div>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={open}
      width={400}
      closable={false}
      className="drawer-sider"
      footer={
        activeFilterCount > 0 ? (
          <button type="button" className="apply-filters-btn" onClick={handleApply}>
            <CheckOutlined /> APPLY {activeFilterCount} FILTER{activeFilterCount > 1 ? 'S' : ''}
          </button>
        ) : null
      }
      styles={{
        body:    { background: 'var(--card-bg)' },
        header:  { background: 'var(--card-bg)' },
        content: { background: 'var(--card-bg)' },
        footer:  { padding: 0, border: 'none' },
      } as any}
    >
      <div className="filter-content">
        {filterItems.map((item, index) => {
          const selectedDateRange = dateFilters[index] ?? null;
          const selectedOption = item.options?.find(o => o.value === selectValues[index]);
          const displayValue = item.type === 'date' && selectedDateRange
            ? formatDateRange(selectedDateRange)
            : item.component === 'group' && selectedGroups.length > 0
              ? `${selectedGroups.length} selected`
              : item.component === 'source' && selectedSources.length > 0
                ? `${selectedSources.length} selected`
                : item.component === 'leadStage' && selectedStages.length > 0
                  ? `${selectedStages.length} selected`
                  : item.component === 'custom' && (customValues[index]?.length ?? 0) > 0
                    ? `${customValues[index].length} selected`
                    : selectedOption
                      ? selectedOption.label
                      : item.value.charAt(0).toUpperCase() + item.value.slice(1);
          const isClickable = item.type === 'date' || item.type === 'team' || item.component === 'group' || item.component === 'source' || item.component === 'leadStage' || item.component === 'custom' || !!item.options;

          return (
            <div
              className="filter-item"
              key={index}
              style={{ cursor: isClickable ? 'pointer' : 'default' }}
              onClick={() => handleFilterClick(item, index)}
            >
              <label className="filter-label">
                {item.label}
                {item.type === 'date' && selectedDateRange && (
                  <span className="clear-date" onClick={(e) => { e.stopPropagation(); handleClearDate(index); }}>Clear</span>
                )}
                {item.type === 'team' && selectedMembers.length > 0 && (
                  <span className="clear-date" onClick={(e) => { e.stopPropagation(); handleClearAssigned(); }}>Clear</span>
                )}
              </label>

              <div>
                {item.type === 'team' && selectedMembers.length > 0 ? (
                  <span className="filter-value assigned-value">
                    <span className="assigned-info">
                      <span className="assigned-avatars">
                        {selectedMembers.slice(0, 3).map((_, i) => (
                          <span className="assigned-avatar" key={i}><UserOutlined /></span>
                        ))}
                      </span>
                      <span className="assigned-text">
                        {selectedMembers.length} Team Member{selectedMembers.length > 1 ? 's' : ''}
                      </span>
                    </span>
                    <div className="icon">
                      <DownOutlined style={{ fontSize: '12px' }} />
                    </div>
                  </span>
                ) : (
                  <span className={`filter-value ${selectedDateRange ? 'has-date' : ''}`}>
                    <span style={{ color: selectedDateRange ? '#3498db' : 'inherit' }}>
                      {item.type === 'date' && selectedDateRange && (
                        <CalendarOutlined style={{ marginRight: '8px', color: '#3498db' }} />
                      )}
                      {displayValue}
                    </span>
                    <div className="icon">
                      <DownOutlined style={{ fontSize: '12px' }} />
                    </div>
                  </span>
                )}
              </div>

              {activeFilter === index && item.type === 'date' && (
                <div className="calendar-popup" onClick={e => e.stopPropagation()}>
                  <Calendar
                    selectedDate={selectedDateRange}
                    presets={item.datePresets}
                    onDateChange={(dateRange: DateRange) => handleDateChange(dateRange, index)}
                  />
                </div>
              )}

              {activeFilter === index && item.options && (
                <div className="filter-radio-popup" onClick={e => e.stopPropagation()}>
                  {item.options.map(opt => {
                    const checked = (selectValues[index] ?? 'any') === opt.value;
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        className={`filter-radio-item ${checked ? 'checked' : ''}`}
                        onClick={() => handleSelectOption(index, opt.value)}
                      >
                        <span className={`filter-radio-dot ${checked ? 'on' : ''}`} />
                        {opt.icon && <span className="filter-radio-icon">{opt.icon}</span>}
                        <span className="filter-radio-label">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {teamMemberModalOpen && item.type === 'team' && (
                <div className="team-member-popup" onClick={e => e.stopPropagation()}>
                  <TeamMemberSelect
                    open={teamMemberModalOpen}
                    onClose={() => setTeamMemberModalOpen(false)}
                    onSelect={handleMemberSelect}
                    selectedMembers={selectedMembers}
                  />
                </div>
              )}

              {groupModalOpen && item.component === 'group' && (
                <div className="group-popup" onClick={e => e.stopPropagation()}>
                  <GroupSelect
                    open={groupModalOpen}
                    onClose={() => setGroupModalOpen(false)}
                    onSelect={setSelectedGroups}
                    selected={selectedGroups}
                  />
                </div>
              )}

              {sourceModalOpen && item.component === 'source' && (
                <div className="group-popup" onClick={e => e.stopPropagation()}>
                  <SourceSelect
                    open={sourceModalOpen}
                    onClose={() => setSourceModalOpen(false)}
                    onSelect={setSelectedSources}
                    selected={selectedSources}
                  />
                </div>
              )}

              {stageModalOpen && item.component === 'leadStage' && (
                <div className="group-popup" onClick={e => e.stopPropagation()}>
                  <LeadStageSelect
                    open={stageModalOpen}
                    onClose={() => setStageModalOpen(false)}
                    onSelect={setSelectedStages}
                    selected={selectedStages}
                  />
                </div>
              )}

              {customModalIndex === index && item.component === 'custom' && (
                <div className="custom-popup" onClick={e => e.stopPropagation()}>
                  <CustomFieldSelect
                    open={customModalIndex === index}
                    onClose={() => setCustomModalIndex(null)}
                    options={item.customOptions}
                    selected={customValues[index] ?? []}
                    onSelect={(ids) => setCustomValues(prev => ({ ...prev, [index]: ids }))}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Drawer>
  );
};

export default FilterDrawer;
